import { useEffect, useState } from 'react'
import { ApiError } from '../lib/api'
import { buyerApi, type BuyerProfile, type ShopItem } from '../lib/buyer'
import { money, num, shortDate } from '../lib/format'
import { Banner, Empty, Loading, Panel, Pill } from '../ui/kit'
import { BuyerHeader } from './BuyerShell'

/**
 * What is for sale today, and the basket.
 *
 * The prices here are the ones the order is charged at — the server reads them from
 * the same place rather than trusting anything this screen sends. A trader should
 * never be able to arrive at a total on their phone and be billed a different one.
 */
export function Shop() {
  const [me, setMe] = useState<BuyerProfile | null>(null)
  const [items, setItems] = useState<ShopItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  /** kg per "cropId|gradeId". */
  const [basket, setBasket] = useState<Record<string, string>>({})
  const [when, setWhen] = useState(() =>
    new Date(Date.now() + 86400000).toISOString().slice(0, 10))
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<{
    code: string; total: number; confirmed: boolean; message: string
  } | null>(null)

  function load() {
    setLoading(true)
    Promise.all([
      buyerApi.get<BuyerProfile>('/shop/me'),
      buyerApi.get<ShopItem[]>('/shop/prices'),
    ])
      .then(([profile, list]) => { setMe(profile); setItems(list) })
      .catch((e) => setError(e?.message ?? 'Could not load the prices.'))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const key = (i: ShopItem) => `${i.cropId}|${i.gradeId}`
  const lines = items
    .map((i) => ({ item: i, kg: Number(basket[key(i)]) || 0 }))
    .filter((l) => l.kg > 0)
  const total = lines.reduce((s, l) => s + l.kg * Number(l.item.pricePerKg), 0)
  const totalKg = lines.reduce((s, l) => s + l.kg, 0)

  async function order() {
    if (lines.length === 0) return
    setBusy(true)
    setError(null)
    try {
      const res = await buyerApi.post<{
        code: string; total: number; confirmed: boolean; message: string
      }>('/shop/orders', {
        deliveryDate: when,
        notes: notes.trim() || null,
        lines: lines.map((l) => ({
          cropId: l.item.cropId, gradeId: l.item.gradeId, kg: l.kg,
        })),
      })
      setDone(res)
      setBasket({})
      setNotes('')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not place that order.')
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <>
        <BuyerHeader />
        <div className="field-greet">
          <h1>Order placed</h1>
          <p>{done.code}</p>
        </div>
        <div className="field-body">
          <div className="brand-card" style={{ alignItems: 'center', textAlign: 'center' }}>
            <span className="micro">{done.confirmed ? 'Confirmed' : 'With our team'}</span>
            <div className="bc-hero num">{money(done.total)}</div>
            <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--on-brand-dim)' }}>
              for {shortDate(when)}
            </p>
          </div>

          <Banner tone={done.confirmed ? 'info' : 'warn'} title={done.message}>
            {done.confirmed
              ? 'You will be charged for what you actually take at the door, not for what was loaded.'
              : 'Nothing has been set aside yet. We will call you to sort it out.'}
          </Banner>

          <button className="btn lg block" onClick={() => { setDone(null); load() }}>
            Buy something else
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      <BuyerHeader />
      <div className="field-greet">
        <h1>What we have today</h1>
        <p>
          {me
            ? <>Delivered to {me.address ?? 'you'}{me.windowStart ? `, ${me.windowStart.slice(0, 5)}–${me.windowEnd?.slice(0, 5)}` : ''}</>
            : 'Prices for today.'}
        </p>
      </div>

      <div className="field-body">
        {loading && <Panel><Loading rows={5} /></Panel>}
        {error && <Banner tone="crit" title={error}>{null}</Banner>}

        {me && !me.canOrder && (
          <Banner tone="info" title="You can look, but somebody else orders">
            You can see prices and what is arriving. Ordering is done by someone else
            at {me.name}.
          </Banner>
        )}

        {me && Number(me.payment.overdue) > 0 && (
          <Banner tone="warn" title={`${money(me.payment.overdue)} is past its due date`}>
            Settling it keeps your orders going out without anybody having to ring you.
          </Banner>
        )}

        {!loading && items.length === 0 && (
          <Empty title="Nothing priced today">
            We have not published today's prices yet. Try again shortly.
          </Empty>
        )}

        {items.length > 0 && (
          <Panel title="Today's prices" meta={`${items.length}`}>
            <div className="panel-body col" style={{ gap: 'var(--sp-5)' }}>
              {items.map((i) => {
                const k = key(i)
                const out = Number(i.availableKg) <= 0
                return (
                  <div key={k} style={{
                    border: '1px solid var(--line)', borderRadius: 'var(--r-md)',
                    padding: 'var(--sp-5) var(--sp-6)', background: 'var(--surface)',
                  }}>
                    <div className="spread" style={{ alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0 }}>
                        <b style={{ fontSize: 'var(--fs-md)' }}>
                          {i.cropName}{i.localName ? ` (${i.localName})` : ''}
                        </b>
                        <div className="faint" style={{ fontSize: 'var(--fs-xs)' }}>
                          Grade {i.gradeCode} — {i.specText}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flex: 'none', paddingLeft: 'var(--sp-5)' }}>
                        <b className="num" style={{ fontSize: 'var(--fs-lg)' }}>
                          {money(i.pricePerKg, { currency: false })}
                        </b>
                        <div className="faint" style={{ fontSize: 'var(--fs-2xs)' }}>per kg</div>
                      </div>
                    </div>

                    <div className="spread" style={{ marginTop: 'var(--sp-5)', gap: 'var(--sp-5)' }}>
                      <span className="faint" style={{ fontSize: 'var(--fs-xs)' }}>
                        {out
                          ? 'None in the store right now'
                          : `${num(i.availableKg, 0)} kg ready`}
                        {i.isYourPrice && <> · <Pill tone="ok" dot={false}>your price</Pill></>}
                      </span>
                      {me?.canOrder && (
                        <input
                          className="input n" type="number" inputMode="decimal" min="0" step="1"
                          placeholder="kg" style={{ width: 90, minHeight: 38 }}
                          value={basket[k] ?? ''}
                          onChange={(e) => setBasket((s) => ({ ...s, [k]: e.target.value }))}
                        />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </Panel>
        )}

        {me?.canOrder && lines.length > 0 && (
          <Panel title="Your order" meta={`${lines.length} item${lines.length === 1 ? '' : 's'}`}>
            <div className="panel-body col" style={{ gap: 'var(--sp-5)' }}>
              {lines.map((l) => (
                <div className="spread" key={key(l.item)} style={{ fontSize: 'var(--fs-sm)' }}>
                  <span>{l.item.cropName} {l.item.gradeCode} · {num(l.kg, 0)} kg</span>
                  <b className="num">{money(l.kg * Number(l.item.pricePerKg), { currency: false })}</b>
                </div>
              ))}

              <div className="divider" />

              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="when">Bring it on</label>
                <input id="when" className="input" type="date" value={when}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setWhen(e.target.value)} />
              </div>

              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="notes">Anything we should know</label>
                <input id="notes" className="input" placeholder="Where to find you, what time…"
                  value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>

              <div className="spread" style={{ fontSize: 'var(--fs-lg)', fontWeight: 700 }}>
                <span>{num(totalKg, 0)} kg</span>
                <span className="num">{money(total)}</span>
              </div>

              <p className="hint">
                This is what you will be charged for what you take. If you send anything
                back at the door, it comes off the bill.
              </p>

              <button className="btn lg block" disabled={busy} onClick={() => void order()}>
                {busy ? 'Sending…' : 'Place the order'}
              </button>
            </div>
          </Panel>
        )}
      </div>
    </>
  )
}
